// =============================================================================
// ui/tabs/ResultsDaily.tsx — the Results board's "Daily" card. #58.
// -----------------------------------------------------------------------------
// A month calendar of the backtest book, one cell per calendar day, plus a
// drawer that lists the samples on the day you click. Ported from beta's
// `renderCal` / `renderDetail` in app/backtest-dashboard.html.
//
// ── ⚠ THIS IS NOT `Calendar.tsx`, AND THE TWO MUST NOT BE MERGED ──
//
// `Calendar.tsx` is the LIVE-TRADE calendar: real money, real accounts, a
// SUN–FRI six-column week, a date window, an account filter and a metrics gate.
// This one is backtests: points, no dollars, no account, no window, and a
// MON–SUN seven-column week. Two calendars that look alike and mean different
// things is exactly the pair you must not fold together — every shared line
// would need a flag, and a flag read the wrong way puts hypothetical points in
// a column a trader reads as money.
//
// ── ⚠ MONDAY-START. THE US DEFAULT WOULD BE WRONG HERE ──
//
// Beta computes `(first.getDay()+6)%7`, which rotates Sunday to the end. This
// port keeps that, and does the whole calculation in UTC — a local-time parse
// rendered in UTC (or the reverse) puts the 1st on the wrong weekday for every
// reader west of Greenwich, which is the same trap `format.ts` documents.
//
// ── ⚠ NAVIGATION ONLY VISITS MONTHS THAT HAVE SAMPLES ──
//
// `months` is the list of months that actually contain samples, so ‹ is dead at
// the first and › is dead at the last. Backtests cluster on the handful of days
// they were run; stepping a month at a time through a book that spans a year is
// stepping through empty grids. Beta restricts it the same way, deliberately.
//
// ── 🚨 TWO NUMBERS BETA SHOWS THAT THIS CARD CANNOT ──
//
// Beta's day cell reads "4 trades · 75%" and its drawer header reads
// "4 trades · +142.5 pts · 75% win". The per-day WIN RATE is not on these props
// — `daily` carries only `{ net, n }` — and `ui/` may not compute it. Both the
// cell's "· 75%" and the header's "· 75% win" are therefore absent, not blank
// and not zero. See `unknowns` in the handoff: the fix is a per-day stat on the
// selector, never an arithmetic in this file.
// =============================================================================

import type { BoardSample } from '../../selectors/backtestBoard';
import { fullDayLabel, num } from '../format';

/** Beta's month names, spelled out — the title reads "September 2026". */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** ⚠ Monday first. Beta's array, in beta's order. */
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * The tint floor and ceiling, straight from beta:
 * `Math.min(.85, .14 + Math.abs(net)/maxAbs * .7)`.
 *
 * ⚠ THE FLOOR IS WHAT MAKES A FLAT DAY VISIBLE. A day with three samples that
 * net exactly zero scales to 0 and would paint nothing — and a blank cell on
 * this grid means "no samples", which is a different and wrong claim. 0.14 is
 * the smallest tint that still reads as a mark.
 *
 * The ceiling stops the biggest day of the month from going fully opaque, so
 * the cell's own text stays legible on top of it.
 */
const TINT_FLOOR = 0.14;
const TINT_CEIL = 0.85;
const TINT_SPAN = 0.7;

/**
 * `+142.5` / `−12.5` / `0.0`.
 *
 * Delegates to `num()` — it is not a second number formatter, only beta's sign
 * prefix in front of one. ⚠ The negative glyph is U+2212 MINUS, not a hyphen,
 * which is what beta's `fmtPtsShort` emits and what `format.ts` already uses for
 * R values elsewhere.
 */
function signedPts(v: number, decimals = 1): string {
  return (v > 0 ? '+' : v < 0 ? '−' : '') + num(Math.abs(v), decimals);
}

/** `+142.5 pts` / `−1 pt`. Beta singularises on the ROUNDED absolute value. */
function signedPtsLong(v: number): string {
  return `${signedPts(v, 1)} pt${Math.abs(Math.round(v)) === 1 ? '' : 's'}`;
}

/**
 * `+1.75R`, or an em-dash.
 *
 * 🚨 NULL IS AN EM-DASH AND NEVER A ZERO. A sample recorded without a stop has
 * no R at all; printing `+0.00R` would report a trade that risked nothing and
 * made nothing, which is a claim about the trade rather than about the record.
 * This is the same null that `selectors/backtestBoard` deliberately passes
 * through where beta substituted 0.
 */
function fmtR(r: number | null): string {
  if (r === null) return '—';
  return (r >= 0 ? '+' : '−') + num(Math.abs(r), 2) + 'R';
}

/** `2026-09` → `{ y: 2026, m: 8 }`, or null if it is not a month key. */
function parseMonth(key: string | undefined): { y: number; m: number } | null {
  if (!key || !/^\d{4}-\d{2}$/.test(key)) return null;
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7)) - 1;
  if (m < 0 || m > 11) return null;
  return { y, m };
}

export interface ResultsDailyProps {
  /** `YYYY-MM`, ascending, ONLY the months that contain samples. */
  months: readonly string[];
  monthIndex: number;
  onMonth: (index: number) => void;
  /** Net points and sample count per ISO day, across the whole filtered book. */
  daily: ReadonlyMap<string, { net: number; n: number }>;
  selectedDay: string | null;
  onSelectDay: (iso: string | null) => void;
  /** Already filtered to `selectedDay` by the parent. */
  daySamples: readonly BoardSample[];
}

export function ResultsDaily(
  { months, monthIndex, onMonth, daily, selectedDay, onSelectDay, daySamples }: ResultsDailyProps,
) {
  const month = parseMonth(months[monthIndex]);
  const atFirst = monthIndex <= 0;
  const atLast = monthIndex >= months.length - 1;

  // ⚠ Beta clears the drill-in when you step months, and this card asks for
  // that rather than assuming the parent does it. Without it the drawer keeps
  // showing a day from the month you just left, under a heading naming the
  // month you are now looking at — two dates on one screen disagreeing.
  const step = (delta: number) => {
    onSelectDay(null);
    onMonth(monthIndex + delta);
  };

  return (
    <div className="bd-dy-card" data-testid="results-daily">
      <div className="bd-dy-bar">
        <span className="bd-dy-eyebrow">Daily</span>
        <div className="bd-dy-mnav">
          <button
            type="button" className="bd-dy-nav-btn" aria-label="Previous month"
            disabled={atFirst} onClick={() => step(-1)}
          >‹</button>
          <button
            type="button" className="bd-dy-nav-btn" aria-label="Next month"
            disabled={atLast} onClick={() => step(1)}
          >›</button>
        </div>
        {/* Beta prints an em-dash for the title when there is no month. */}
        <div className="bd-dy-mtitle">
          {month ? `${MONTH_NAMES[month.m] ?? ''} ${month.y}` : '—'}
        </div>
        <div className="bd-dy-legend">
          <span>Loss</span>
          <i className="bd-dy-sw bd-dy-sw-loss" />
          <i className="bd-dy-sw bd-dy-sw-mid" />
          <i className="bd-dy-sw bd-dy-sw-win" />
          <span>Win · click a day to drill in</span>
        </div>
      </div>

      {month
        ? <MonthGrid
            month={month} daily={daily}
            selectedDay={selectedDay} onSelectDay={onSelectDay}
          />
        : <div className="bd-dy-none">No dated samples in this filter.</div>}

      {/*
        ⚠ THE DRAWER IS ALWAYS IN THE TREE, open or shut. Beta animates it by
        toggling a class on a `max-height: 0` box; unmounting it instead would
        make it appear and vanish with no transition, and the drawer opening
        below the fold with no motion is the reason a click reads as "nothing
        happened".
      */}
      <div className={`bd-dy-detail${selectedDay ? ' bd-dy-open' : ''}`}>
        <div className="bd-dy-detail-in">
          {selectedDay
            ? <DayDetail
                iso={selectedDay}
                net={daily.get(selectedDay)?.net ?? null}
                samples={daySamples}
              />
            : null}
        </div>
      </div>
    </div>
  );
}

function MonthGrid(
  { month, daily, selectedDay, onSelectDay }: {
    month: { y: number; m: number };
    daily: ReadonlyMap<string, { net: number; n: number }>;
    selectedDay: string | null;
    onSelectDay: (iso: string | null) => void;
  },
) {
  const { y, m } = month;
  const prefix = `${y}-${String(m + 1).padStart(2, '0')}`;

  // ⚠ UTC throughout — see the header. `getUTCDay()` returns 0 for Sunday, and
  // `+6 % 7` rotates it to column 6 so Monday lands in column 0.
  const startDow = (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7;
  // Day 0 of the NEXT month is the last day of this one.
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

  // The bar-scale for the tint: the biggest absolute day in THIS month only.
  //
  // ⚠ This is the "percentage width of a bar" case, not a derived statistic —
  // the values are `daily`'s, already computed, and this only picks the largest
  // so the shading has something to be relative to. Beta floors it at 1, which
  // also stops a month where every day nets exactly zero from dividing by zero.
  let maxAbs = 1;
  for (const [iso, d] of daily) {
    if (iso.startsWith(prefix)) maxAbs = Math.max(maxAbs, Math.abs(d.net));
  }

  const cells = [];
  for (let i = 0; i < startDow; i += 1) {
    cells.push(<div key={`pad-${i}`} className="bd-dy-day bd-dy-empty" />);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    const iso = `${prefix}-${String(d).padStart(2, '0')}`;
    const day = daily.get(iso);

    // A day with no samples: the date, and nothing else. Not clickable — there
    // is nothing to drill into, unlike the live calendar where an untraded day
    // still has a plan behind it.
    if (!day) {
      cells.push(
        <div key={iso} className="bd-dy-day">
          <div className="bd-dy-date">{d}</div>
        </div>,
      );
      continue;
    }

    const intensity = Math.min(TINT_CEIL, TINT_FLOOR + (Math.abs(day.net) / maxAbs) * TINT_SPAN);
    const open = () => onSelectDay(selectedDay === iso ? null : iso);
    const cls = ['bd-dy-day', 'bd-dy-has'];
    if (selectedDay === iso) cls.push('bd-dy-sel');

    cells.push(
      <div
        key={iso}
        className={cls.join(' ')}
        data-day={iso}
        role="button"
        tabIndex={0}
        aria-label={`${iso}, ${signedPtsLong(day.net)}`}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        }}
      >
        <div className="bd-dy-date">{d}</div>
        <div className={`bd-dy-pts ${day.net >= 0 ? 'bd-dy-pos' : 'bd-dy-neg'}`}>
          {signedPts(day.net, 1)}
        </div>
        {/* 🚨 Beta also prints the day's win rate here. It is not on these
            props and `ui/` may not compute it — see the header. */}
        {/* 🚨 SAMPLES, NOT TRADES. Beta says "trades" here, and on a journal
            where that word means real money it is the wrong one — none of these
            happened. The KPI band already says "Samples"; a board that calls the
            same thing two names invites the reader to think they are two things. */}
        <div className="bd-dy-meta">{day.n} sample{day.n === 1 ? '' : 's'}</div>
        {/*
          The tint. ⚠ Beta writes `rgba(46,157,255, α)` inline; this sets the
          COLOUR from a class and only the alpha inline, so the palette stays in
          the stylesheet where the rest of the board's colours live. On a solid
          3px strip an `opacity` is what an `rgba` alpha renders as anyway.
        */}
        <div
          className={`bd-dy-strip ${day.net >= 0 ? 'bd-dy-strip-win' : 'bd-dy-strip-loss'}`}
          style={{ opacity: intensity }}
        />
      </div>,
    );
  }

  return (
    <div className="bd-dy-grid">
      {DOW.map((d) => <div key={d} className="bd-dy-dow">{d}</div>)}
      {cells}
    </div>
  );
}

function DayDetail(
  { iso, net, samples }: { iso: string; net: number | null; samples: readonly BoardSample[] },
) {
  return (
    <>
      <div className="bd-dy-detail-h">
        {/*
          ⚠ Beta's heading is "Wednesday, September 3" — this adds the year,
          because `fullDayLabel` is the shared formatter for a spelled-out date
          and writing a fourth date formatter to shave ", 2026" is the wrong
          trade. A backtest book spans years, and the month title that supplies
          the year is off-screen once the drawer is scrolled to.
        */}
        <h4>{fullDayLabel(iso)}</h4>
        <span className="bd-dy-dsum">
          {/* ⚠ Beta prints "1 trades" here — its own calendar cell singularises
              and this header does not. Singularised deliberately; revert by
              deleting the conditional if the diff harness pins beta's wording.
              🚨 Beta also appends "· 75% win"; that number is not on the props. */}
          {samples.length} sample{samples.length === 1 ? '' : 's'}
          {net === null ? null : (
            <> · <b className={net >= 0 ? 'bd-dy-pos' : 'bd-dy-neg'}>{signedPtsLong(net)}</b></>
          )}
        </span>
      </div>

      {/* ⚠ The table scrolls sideways INSIDE this box. Nine columns on a phone
          would otherwise widen the whole board and scroll the page. */}
      <div className="bd-dy-scroll">
        <table className="bd-dy-table">
          <thead>
            <tr>
              <th className="bd-dy-l">Pattern</th>
              <th className="bd-dy-l">Instr</th>
              <th className="bd-dy-l">Session</th>
              <th>Points</th>
              <th>R</th>
              <th>MAE</th>
              <th>MFE</th>
              <th>Confluences</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {samples.map((s, i) => (
              // ⚠ Index in the key. `BoardSample` carries no id, and one day
              // can hold two samples of the same pattern on the same instrument
              // — a date+pattern key would collide and React would reuse the
              // wrong row. The list is never reordered, only replaced wholesale
              // when the day changes, so the index is stable for its lifetime.
              <tr key={`${s.patternId}-${i}`}>
                <td className="bd-dy-l">{s.patternName}</td>
                <td className="bd-dy-l">{s.instrument}</td>
                {/* A sample saved with no session shows an em-dash. The board's
                    Timing card drops these rows entirely; here the row exists
                    and only the one field is missing, so say which one. */}
                <td className="bd-dy-l bd-dy-muted">{s.session || '—'}</td>
                <td className={`bd-dy-strong ${s.points >= 0 ? 'bd-dy-pos' : 'bd-dy-neg'}`}>
                  {signedPts(s.points, 1)}
                </td>
                <td className={s.r === null ? 'bd-dy-muted' : s.r >= 0 ? 'bd-dy-pos' : 'bd-dy-neg'}>
                  {fmtR(s.r)}
                </td>
                {/* MAE and MFE arrive as magnitudes — `toBoardSamples` takes the
                    absolute value — so they are printed unsigned, on purpose.
                    An MAE shown as "−8.0" reads as a loss rather than as how far
                    the trade went against you before it worked. */}
                <td className="bd-dy-muted">{num(s.mae, 1)}</td>
                <td className="bd-dy-muted">{num(s.mfe, 1)}</td>
                <td>
                  <div className="bd-dy-chips">
                    {s.confluences.length === 0
                      ? <span className="bd-dy-muted">—</span>
                      /* ⚠ THE NAME, NOT THE ID — the same defect the board's
                         rows had, in a place the card did not name. These chips
                         printed the raw stored code, so a renamed confluence
                         showed its old internal id here for ever. `orphan`
                         marks one the registry no longer holds; it keeps its id
                         rather than vanishing, because the sample still
                         happened. */
                      : s.confluences.map((c) => (
                          <span
                            key={c.id}
                            className={`bd-dy-chip${c.orphan ? ' orphan' : ''}`}
                          >{c.name}</span>
                        ))}
                  </div>
                </td>
                {/* 🚨 The outcome dot is the ONLY thing separating a SCRATCH
                    from a win that happened to net 0.0. Both print "0.0" in the
                    points column, and they are not the same trade — the win
                    rate counts one and excludes the other. */}
                <td>
                  <span className={`bd-dy-out bd-dy-out-${s.outcome}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
